import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { authClient } from '@/lib/auth-client'
import { useUploadFile } from '@/lib/upload'
import { Button } from '@deepguide-ai/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog'
import { PolicyReviewHistory } from '@/components/screenata/policy-detail/PolicyReviewHistory'
import { ReviewContextFields } from '@/components/screenata/review/ReviewContextFields'

export function DocumentReviewHistory({ documentId, workspaceId, readOnly = false, versionId }: {
  documentId: string; workspaceId: string; readOnly?: boolean; versionId?: string
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<'confirm' | 'history' | null>(null)
  const [notes, setNotes] = useState('')
  const [participants, setParticipants] = useState<string[]>([])
  const [attachments, setAttachments] = useState<Array<{ id: string; name: string }>>([])
  const target = useRef<{ versionId: string; version: number } | null>(null)
  const requestId = useRef('')
  const { data: session } = authClient.useSession()
  const members = useQuery({ ...trpc.workspaceMembers.list.queryOptions(), enabled: dialog === 'confirm' })
  const history = useQuery(trpc.vaultDocument.reviewHistory.queryOptions({ id: documentId }))
  const { uploadFile, isUploading } = useUploadFile({ onUploadComplete: file => setAttachments(current => [...current, { id: file.id, name: file.name }]) })
  const record = useMutation(trpc.vaultDocument.recordReview.mutationOptions({
    onSuccess: () => {
      toast.success('Document review recorded')
      queryClient.invalidateQueries({ queryKey: trpc.inbox.list.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.inbox.badgeCount.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.inbox.unreadCount.queryKey() })
      setDialog(null)
      queryClient.invalidateQueries({ queryKey: trpc.vaultDocument.reviewHistory.queryKey({ id: documentId }) })
      queryClient.invalidateQueries({ queryKey: trpc.vaultDocument.getById.queryKey({ id: documentId }) })
    },
    onError: error => toast.error(error.message),
  }))
  const reviewable = !readOnly && !versionId && history.data?.canReview && history.data.reviewTarget
  const currentReview = history.data?.records.find(entry => entry.documentVersionId === history.data?.reviewTarget?.versionId)
  return <>
    {currentReview && <span className='hidden text-xs text-muted-foreground xl:inline'>Last review {new Date(currentReview.reviewedAt).toLocaleDateString()}</span>}
    <Button variant='ghost' size='sm' disabled={history.isLoading} onClick={() => setDialog('history')}>Review history</Button>
    {reviewable && <Button variant='outline' size='sm' onClick={() => {
      target.current = history.data!.reviewTarget
      requestId.current = crypto.randomUUID()
      setNotes(''); setParticipants([]); setAttachments([]); setDialog('confirm')
    }}>Review</Button>}
    <Dialog open={dialog !== null} onOpenChange={open => { if (!open && !record.isPending && !isUploading) setDialog(null) }}>
      <DialogContent className='max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{dialog === 'confirm' ? 'Confirm document review' : 'Review history'}</DialogTitle>
          <DialogDescription>{dialog === 'confirm'
            ? `I have reviewed saved v${target.current?.version} and confirm that no changes are required.`
            : 'Version-specific reviews of this document, separate from Test and Control approvals.'}</DialogDescription>
        </DialogHeader>
        <div className='space-y-3 px-6'>
          {history.error && <p role='alert' className='text-sm text-destructive'>{history.error.message}</p>}
          {dialog === 'confirm' ? <div className='space-y-4'>
            <div className='space-y-1 text-sm'>
              <p className='font-medium'>{history.data?.title} · Saved v{target.current?.version}</p>
              <p className='text-muted-foreground'>Reviewed by {session?.user.name || session?.user.email} (you)</p>
            </div>
            <p className='text-sm text-muted-foreground'>Check that responsibilities, named owners and scope remain accurate. If changes are needed, cancel and edit the document first.</p>
            <ReviewContextFields idPrefix='document-review' notes={notes} setNotes={setNotes} participants={participants} setParticipants={setParticipants}
              attachments={attachments} removeAttachment={id => setAttachments(current => current.filter(file => file.id !== id))} uploadFile={uploadFile}
              pending={record.isPending} isUploading={isUploading} currentUserId={session?.user.id}
              members={members.data} membersLoading={members.isLoading} membersError={members.error} />
            <p className='text-sm text-muted-foreground'>Your identity and the confirmation time will be recorded. This does not change the document version or submit or approve a Test.</p>
          </div> : history.data && <>
            <PolicyReviewHistory records={history.data.records} onViewVersion={version => {
              const entry = history.data?.records.find(record => record.version === version)
              if (!entry) return
              void navigate({ to: '/workspaces/$workspaceId/documents/$docId', params: { workspaceId, docId: documentId }, search: { versionId: entry.documentVersionId } })
              setDialog(null)
            }} />
            {!history.data.reviewTarget && <p className='text-sm text-muted-foreground'>Save an active document version before recording a review.</p>}
            {history.data.targets.length > 0 && <div className='space-y-2 border-t pt-3'>
              <p className='text-sm font-medium'>Related Tests and renewal dates</p>
              <p className='text-xs text-muted-foreground'>Renewal dates belong to the related Tests. A document review records your confirmation but does not submit or approve a Test.</p>
              {history.data.targets.map(test => <p key={test.id} className='text-sm'>{test.title} · {test.nextRetestAt ? `Due ${new Date(test.nextRetestAt).toLocaleDateString()}` : 'No renewal date scheduled'}</p>)}
            </div>}
          </>}
        </div>
        <DialogFooter>
          <Button variant='outline' disabled={record.isPending || isUploading} onClick={() => setDialog(null)}>{dialog === 'confirm' ? 'Cancel' : 'Close'}</Button>
          {dialog === 'confirm' && <Button disabled={record.isPending || isUploading || !reviewable || history.data?.reviewTarget?.versionId !== target.current?.versionId} onClick={() => {
            if (!target.current) return
            record.mutate({ documentId, documentVersionId: target.current.versionId, requestId: requestId.current, notes, participants, fileIds: attachments.map(file => file.id) })
          }}>{record.isPending ? 'Recording…' : 'Confirm review'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
}
